import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail, getToken, setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = user

  const checkAuth = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const persist = useCallback((data) => {
    if (data?.token) setToken(data.token);
    const { token, ...u } = data || {};
    setUser(u);
    return u;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    sessionStorage.removeItem("ethara_splash_shown"); // trigger splash on next render
    return persist(data);
  }, [persist]);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    sessionStorage.removeItem("ethara_splash_shown");
    return persist(data);
  }, [persist]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout API call failed, clearing session locally", e);
    }
    clearToken();
    sessionStorage.removeItem("ethara_splash_shown");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, formatApiErrorDetail }),
    [user, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
